// src/pages/DeepawaliCarnivalPage/DeepawaliCarnivalPage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import LEKudluLogo from '../../assets/images/LEKudluLogoTSP.svg';
import './DeepawaliCarnivalPage.css';

const DeepawaliCarnivalPage = () => {
    const [currentSection, setCurrentSection] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isEntered, setIsEntered] = useState(false);
    const [hoveredStall, setHoveredStall] = useState(null);
    const containerRef = useRef(null);
    const heroRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const minSwipeDistance = 50;

    const sections = [
        {
            id: 'entry',
            title: 'Deepawali Carnival',
            subtitle: 'A Celebration of Lights, Food & Fun',
            date: 'November 1st, 2025'
        },
        {
            id: 'welcome',
            title: 'Welcome to the Festival',
            content: 'Join us for a spectacular Deepawali celebration filled with delicious food, creative arts, fun games, and shopping! Explore amazing stalls, enjoy tasty treats, and create beautiful memories with your family.',
            kannadaText: 'ದೀಪಾವಳಿಯ ಹಬ್ಬದ ಶುಭಾಶಯಗಳು'
        },
        {
            id: 'stalls',
            title: 'Explore Our Stalls',
            stalls: [
                {
                    name: 'Sweet Treats & Bakes',
                    icon: '⬢',
                    description: 'Delicious cookies, cupcakes, brownies, cakes, and homemade chocolates',
                    color: 'from-pink-500 to-orange-500',
                    theme: 'food'
                },
                {
                    name: 'Pickles & Snacks',
                    icon: '◈',
                    description: 'Homemade pickles and savory snack items',
                    color: 'from-green-500 to-yellow-500',
                    theme: 'food'
                },
                {
                    name: 'Saree Corner',
                    icon: '❖',
                    description: 'Beautiful collection of sarees and blouses',
                    color: 'from-purple-500 to-pink-500',
                    theme: 'shopping'
                },
                {
                    name: 'Art Gallery',
                    icon: '✦',
                    description: 'Netipattam, Lippon Art, Mandala, paintings, and decorative hangings',
                    color: 'from-indigo-500 to-purple-500',
                    theme: 'art'
                },
                {
                    name: 'Food Fiesta',
                    icon: '◆',
                    description: 'Noodles, Manchurian, Pav Bhaji, and tasty laddoos',
                    color: 'from-red-500 to-orange-500',
                    theme: 'food'
                },
                {
                    name: 'Refreshments',
                    icon: '◇',
                    description: 'Flavored milk, fresh smoothies, and delicious shakes',
                    color: 'from-blue-400 to-cyan-500',
                    theme: 'food'
                },
                {
                    name: 'Organic Delights',
                    icon: '✧',
                    description: 'Healthy millet items, organic sweets, and nutritious groceries',
                    color: 'from-green-600 to-lime-500',
                    theme: 'food'
                },
                {
                    name: 'Games Zone',
                    icon: '◉',
                    description: 'Fun games and exciting activities for everyone',
                    color: 'from-yellow-500 to-red-500',
                    theme: 'entertainment'
                },
                {
                    name: 'Tattoo & Art',
                    icon: '✪',
                    description: 'Professional tattoo artists and creative caricature portraits',
                    color: 'from-violet-500 to-fuchsia-500',
                    theme: 'entertainment'
                },
                {
                    name: 'Stationery Corner',
                    icon: '◎',
                    description: 'School supplies and office stationery items',
                    color: 'from-blue-500 to-indigo-500',
                    theme: 'shopping'
                },
                {
                    name: 'Fashion Hub',
                    icon: '◐',
                    description: 'Dress materials, elegant suits, stylish bags, and Hyderabad jewellery',
                    color: 'from-rose-500 to-pink-600',
                    theme: 'shopping'
                },
                {
                    name: 'Natural Care',
                    icon: '◑',
                    description: 'Handcrafted soaps and natural perfumes',
                    color: 'from-teal-500 to-emerald-500',
                    theme: 'shopping'
                }
            ]
        },
        {
            id: 'details',
            title: 'Event Details',
            details: {
                date: 'November 1st, 2025',
                time: '10:30 AM onwards',
                venue: 'Little Elly Kudlu Campus',
                dress: 'Traditional Indian Attire'
            }
        },
        {
            id: 'rajyotsava',
            title: 'Karnataka Rajyotsava',
            kannadaWish: 'ಕನ್ನಡ ರಾಜ್ಯೋತ್ಸವದ ಹಾರ್ದಿಕ ಶುಭಾಶಯಗಳು',
            englishWish: 'Happy Karnataka Formation Day',
            description: 'Celebrating the pride and culture of Karnataka as we unite for this special occasion'
        },
        {
            id: 'register',
            title: 'Register Now',
            content: 'Register your family for the Deepawali Carnival! Get your exclusive digital pass with QR code for easy entry and access to all the festivities.'
        }
    ];

    const onTouchStart = (e) => {
        // Check if touch started on a scrollable element (stalls grid or details container)
        const target = e.target;
        const isScrollableArea = target.closest('.stalls-grid') || target.closest('.details-container');

        if (isScrollableArea) {
            // Don't capture touch for section navigation if in scrollable area
            setTouchStart(null);
            setTouchEnd(null);
            return;
        }

        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientY);
    };

    const onTouchMove = (e) => {
        if (!touchStart) return; // Don't track if we're in a scrollable area
        setTouchEnd(e.targetTouches[0].clientY);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isSwipeUp = distance > minSwipeDistance;
        const isSwipeDown = distance < -minSwipeDistance;

        if (isSwipeUp) {
            goToNextSection();
        }
        if (isSwipeDown) {
            goToPrevSection();
        }
    };

    const goToNextSection = () => {
        if (currentSection < sections.length - 1) {
            setCurrentSection(prev => prev + 1);
        }
    };

    const goToPrevSection = () => {
        if (currentSection > 0) {
            setCurrentSection(prev => prev - 1);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            goToNextSection();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            goToPrevSection();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [currentSection]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.transform = `translateY(-${currentSection * 100}vh)`;
        }
    }, [currentSection]);

    // Hide TARS chatbot on carnival pages
    useEffect(() => {
        const tarsWidget = document.querySelector('[id*="tars"]') ||
                          document.querySelector('[class*="tars"]') ||
                          document.querySelector('iframe[src*="tars"]');
        if (tarsWidget) {
            tarsWidget.style.display = 'none';
        }

        return () => {
            // Re-show on unmount if it exists
            if (tarsWidget) {
                tarsWidget.style.display = '';
            }
        };
    }, []);

    const handleEnter = () => {
        setIsEntered(true);
        setCurrentSection(1);
    };

    const renderFloatingElements = () => {
        const elements = ['◆', '✦', '✧', '❖', '◈'];
        return Array.from({ length: 30 }).map((_, i) => (
            <motion.div
                key={i}
                className="floating-element"
                initial={{ y: '100vh', x: 0, rotate: 0, opacity: 0 }}
                animate={{
                    y: '-100vh',
                    x: Math.sin(i) * 100,
                    rotate: 360,
                    opacity: [0, 0.6, 0]
                }}
                transition={{
                    duration: 10 + Math.random() * 10,
                    repeat: Infinity,
                    delay: Math.random() * 5,
                    ease: "linear"
                }}
                style={{
                    left: `${Math.random() * 100}%`,
                    position: 'absolute',
                    fontSize: '1.5rem',
                    pointerEvents: 'none',
                    zIndex: 1
                }}
            >
                {elements[i % elements.length]}
            </motion.div>
        ));
    };

    return (
        <div
            className="deepawali-carnival"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {renderFloatingElements()}

            <div className="section-indicators">
                {sections.map((_, index) => (
                    <motion.button
                        key={index}
                        className={`indicator ${currentSection === index ? 'active' : ''}`}
                        onClick={() => setCurrentSection(index)}
                        aria-label={`Go to section ${index + 1}`}
                        whileHover={{ scale: 1.5 }}
                        whileTap={{ scale: 0.9 }}
                    />
                ))}
            </div>

            {/* Navigation Buttons */}
            <div className="navigation-controls">
                {currentSection > 0 && (
                    <motion.button
                        className="nav-button nav-prev"
                        onClick={goToPrevSection}
                        aria-label="Previous section"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        whileHover={{ scale: 1.1, x: -5 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <span className="nav-arrow">←</span>
                        <span className="nav-label">Previous</span>
                    </motion.button>
                )}

                {currentSection < sections.length - 1 && (
                    <motion.button
                        className="nav-button nav-next"
                        onClick={goToNextSection}
                        aria-label="Next section"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        whileHover={{ scale: 1.1, x: 5 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <span className="nav-label">Next</span>
                        <span className="nav-arrow">→</span>
                    </motion.button>
                )}
            </div>

            <div className="sections-container" ref={containerRef}>
                {/* Entry Section */}
                <section className="carnival-section entry-section" ref={heroRef}>
                    <motion.div
                        className="entry-content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                    >
                        <motion.div
                            className="logo-container"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{
                                type: "spring",
                                stiffness: 260,
                                damping: 20,
                                delay: 0.2
                            }}
                        >
                            <img src={LEKudluLogo} alt="Little Elly Kudlu" className="carnival-logo" />
                        </motion.div>

                        <motion.div
                            className="diya-animation"
                            animate={{
                                y: [0, -20, 0],
                                scale: [1, 1.05, 1]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <motion.span
                                className="diya-large"
                                animate={{
                                    filter: [
                                        'drop-shadow(0 0 30px #FDB750)',
                                        'drop-shadow(0 0 60px #FF6B35)',
                                        'drop-shadow(0 0 30px #FDB750)'
                                    ]
                                }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                ✦
                            </motion.span>
                            <motion.div
                                className="flame"
                                animate={{
                                    scaleY: [1, 1.2, 1],
                                    scaleX: [1, 0.9, 1],
                                    y: [0, -5, 0]
                                }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            />
                        </motion.div>

                        <motion.h1
                            className="entry-title"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                        >
                            Deepawali Carnival
                        </motion.h1>

                        <motion.p
                            className="entry-subtitle"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.8 }}
                        >
                            A Celebration of Lights, Food & Fun
                        </motion.p>

                        <motion.div
                            className="entry-date"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.9, duration: 0.8 }}
                        >
                            November 1st, 2025 | 10:30 AM onwards
                        </motion.div>

                        <motion.button
                            className="enter-button"
                            onClick={handleEnter}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 1.1, duration: 0.8 }}
                            whileHover={{
                                scale: 1.05,
                                boxShadow: "0 20px 50px rgba(255, 107, 53, 0.8)"
                            }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span>Enter the Celebration</span>
                            <motion.span
                                className="arrow"
                                animate={{ x: [0, 5, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            >
                                →
                            </motion.span>
                        </motion.button>
                    </motion.div>

                    <motion.div
                        className="mandala-bg"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    />

                    <motion.div
                        className="mandala-bg-2"
                        animate={{ rotate: -360 }}
                        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                    />
                </section>

                {/* Welcome Section */}
                <section className="carnival-section welcome-section">
                    <motion.div
                        className="section-content"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: false }}
                    >
                        <motion.h2
                            className="section-heading"
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            {sections[1].title}
                        </motion.h2>

                        <motion.p
                            className="kannada-greeting"
                            initial={{ x: -50, opacity: 0 }}
                            whileInView={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            {sections[1].kannadaText}
                        </motion.p>

                        <motion.p
                            className="section-text"
                            initial={{ y: 30, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            {sections[1].content}
                        </motion.p>

                        <div className="decorative-elements">
                            {['✧', '✦', '◆', '❖', '✧'].map((emoji, i) => (
                                <motion.span
                                    key={i}
                                    className="decoration"
                                    initial={{ scale: 0, rotate: -180 }}
                                    whileInView={{ scale: 1, rotate: 0 }}
                                    transition={{
                                        delay: 0.6 + i * 0.1,
                                        type: "spring",
                                        stiffness: 200
                                    }}
                                    animate={{
                                        y: [0, -15, 0],
                                        rotate: [0, 10, -10, 0]
                                    }}
                                    transition={{
                                        y: { duration: 2, repeat: Infinity, delay: i * 0.2 },
                                        rotate: { duration: 3, repeat: Infinity, delay: i * 0.2 }
                                    }}
                                >
                                    {emoji}
                                </motion.span>
                            ))}
                        </div>
                    </motion.div>
                </section>

                {/* Stalls Section */}
                <section className="carnival-section stalls-section">
                    <motion.div
                        className="section-content"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.h2
                            className="section-heading"
                            initial={{ y: -30, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            {sections[2].title}
                        </motion.h2>

                        <motion.div
                            className="scroll-hint-stalls"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            ↓ Scroll to see all 12 stalls ↓
                        </motion.div>

                        <div className="stalls-grid">
                            {sections[2].stalls.map((stall, index) => (
                                <motion.div
                                    key={index}
                                    className={`stall-card ${stall.theme}`}
                                    initial={{
                                        opacity: 0,
                                        y: 50,
                                        rotateX: -15
                                    }}
                                    whileInView={{
                                        opacity: 1,
                                        y: 0,
                                        rotateX: 0
                                    }}
                                    transition={{
                                        delay: index * 0.1,
                                        duration: 0.6,
                                        type: "spring",
                                        stiffness: 100
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        rotateY: hoveredStall === index ? 180 : 0,
                                        z: 50
                                    }}
                                    onHoverStart={() => setHoveredStall(index)}
                                    onHoverEnd={() => setHoveredStall(null)}
                                    style={{
                                        transformStyle: 'preserve-3d',
                                        perspective: 1000
                                    }}
                                >
                                    <motion.div
                                        className="stall-front"
                                        style={{
                                            backfaceVisibility: 'hidden',
                                            position: 'relative'
                                        }}
                                    >
                                        <motion.div
                                            className="stall-icon"
                                            animate={{
                                                rotate: hoveredStall === index ? 360 : 0,
                                                scale: hoveredStall === index ? 1.2 : 1
                                            }}
                                            transition={{ duration: 0.6 }}
                                        >
                                            {stall.icon}
                                        </motion.div>
                                        <h3 className="stall-name">{stall.name}</h3>
                                        <p className="stall-description">{stall.description}</p>

                                        <motion.div
                                            className={`theme-badge ${stall.theme}-badge`}
                                            initial={{ scale: 0 }}
                                            whileInView={{ scale: 1 }}
                                            transition={{ delay: 0.3 }}
                                        >
                                            {stall.theme === 'food' && '◆ Food'}
                                            {stall.theme === 'shopping' && '◈ Shopping'}
                                            {stall.theme === 'entertainment' && '✦ Fun'}
                                            {stall.theme === 'art' && '✧ Art'}
                                        </motion.div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </section>

                {/* Details Section */}
                <section className="carnival-section details-section">
                    <motion.div
                        className="section-content"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.h2
                            className="section-heading"
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            {sections[3].title}
                        </motion.h2>

                        <div className="details-container">
                            {Object.entries(sections[3].details).map(([key, value], index) => {
                                const icons = { date: '◆', time: '◇', venue: '◈', dress: '❖' };
                                const labels = { date: 'DATE', time: 'TIME', venue: 'VENUE', dress: 'DRESS CODE' };
                                return (
                                    <motion.div
                                        key={key}
                                        className="detail-item"
                                        initial={{
                                            opacity: 0,
                                            y: 30,
                                            scale: 0.8
                                        }}
                                        whileInView={{
                                            opacity: 1,
                                            y: 0,
                                            scale: 1
                                        }}
                                        transition={{
                                            delay: index * 0.1,
                                            duration: 0.5
                                        }}
                                        whileHover={{
                                            scale: 1.05,
                                            transition: { duration: 0.3 }
                                        }}
                                    >
                                        <motion.div
                                            className="detail-icon"
                                            animate={{
                                                scale: [1, 1.1, 1]
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                delay: index * 0.3
                                            }}
                                        >
                                            {icons[key]}
                                        </motion.div>
                                        <div className="detail-label">{labels[key]}</div>
                                        <div className="detail-value">{value}</div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        <motion.div
                            className="venue-highlight"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <img src={LEKudluLogo} alt="Little Elly Kudlu" className="venue-logo" />
                            <p className="venue-text">Little Elly Kudlu Campus</p>
                        </motion.div>
                    </motion.div>
                </section>

                {/* Rajyotsava Section */}
                <section className="carnival-section rajyotsava-section">
                    <motion.div
                        className="section-content"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.h2
                            className="section-heading"
                            initial={{ y: -30, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            {sections[4].title}
                        </motion.h2>

                        <motion.div
                            className="final-message"
                            initial={{ scale: 0.9, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                        >
                            <motion.p
                                className="kannada-wish"
                                animate={{
                                    textShadow: [
                                        '0 0 20px rgba(255, 199, 44, 0.5)',
                                        '0 0 40px rgba(255, 36, 0, 0.8)',
                                        '0 0 20px rgba(255, 199, 44, 0.5)'
                                    ]
                                }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                {sections[4].kannadaWish}
                            </motion.p>
                            <p className="translation">{sections[4].englishWish}</p>
                        </motion.div>

                        <motion.p
                            className="section-text"
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            {sections[4].description}
                        </motion.p>

                        <div className="bottom-decoration">
                            {['◆', '◇', '◈', '◇', '◆'].map((emoji, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ delay: 0.6 + i * 0.1 }}
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        opacity: [1, 0.7, 1]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        delay: i * 0.2
                                    }}
                                >
                                    {emoji}
                                </motion.span>
                            ))}
                        </div>
                    </motion.div>
                </section>

                {/* Registration Section */}
                <section className="carnival-section register-section">
                    <motion.div
                        className="section-content"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <motion.h2
                            className="section-heading"
                            initial={{ y: -30, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            {sections[5].title}
                        </motion.h2>

                        <motion.p
                            className="section-text"
                            initial={{ y: 20, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            {sections[5].content}
                        </motion.p>

                        <motion.div
                            className="register-button-container"
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.4, type: "spring" }}
                        >
                            <motion.button
                                className="register-cta-button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => window.location.href = '/deepawalicarnival/register'}
                            >
                                <span className="button-text">Register Your Family</span>
                                <span className="button-icon">→</span>
                            </motion.button>
                        </motion.div>

                        <div className="bottom-decoration">
                            {['✧', '✦', '◆', '✦', '✧'].map((emoji, i) => (
                                <motion.span
                                    key={i}
                                    className={i === 2 ? 'center-glyph' : 'side-glyph'}
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ delay: 0.6 + i * 0.1 }}
                                    animate={{
                                        y: i === 2 ? [0, -10, 0] : 0,
                                        opacity: i === 2 ? 1 : [1, 0.3, 1],
                                        scale: i === 2 ? [1, 1.1, 1] : [1, 0.8, 1]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        delay: i * 0.2
                                    }}
                                >
                                    {emoji}
                                </motion.span>
                            ))}
                        </div>

                        <motion.div
                            className="organized-by"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            <p>Organized by</p>
                            <motion.img
                                src={LEKudluLogo}
                                alt="Little Elly Kudlu"
                                className="footer-logo"
                                whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                            />
                        </motion.div>

                        <motion.div
                            className="crafted-by"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 1 }}
                        >
                            <p>Crafted by <a href="https://neuralway.ai" target="_blank" rel="noopener noreferrer">neuralway.ai</a></p>
                        </motion.div>
                    </motion.div>
                </section>
            </div>
        </div>
    );
};

export default DeepawaliCarnivalPage;
